import { useQuery } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { formatMoney, formatPercent } from '../lib/format';
import { FeatureTips } from '../components/FeatureTips';
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Metric,
  PageHeader,
  ProgressBar,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * §29.5 — the finance view, which is all sys's home page is. This was the
 * Finance block at the bottom of the admin dashboard; the admin keeps the
 * operations blocks, and this reads the same endpoint the block did.
 */
export function DashboardPage() {
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();

  const { data: finance, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard', 'finance'],
    queryFn: () => api.get<any>('/dashboard/finance'),
    refetchInterval: 60_000,
  });

  // What's new sits under the title whatever the figures do: it does not wait for them.
  const header = (
    <>
      <PageHeader
        title={t('dash.title')}
        subtitle={`${user?.fullName} · ${user?.roles.map((r) => (locale === 'ar' ? r.nameAr ?? r.name : r.name)).join(', ')}`}
      />
      <FeatureTips />
    </>
  );

  if (isLoading) return <>{header}<LoadingBlock /></>;
  if (error) return <>{header}<ErrorState error={error} onRetry={() => void refetch()} /></>;
  // The API answers null to a session with no finance permission at all.
  if (!finance) return <>{header}<Card><EmptyState title={t('common.noResults')} hint="" /></Card></>;

  return (
    <>
      {header}

      <section>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Metric
            label={t('dash.monthExpenses')}
            value={formatMoney(finance.monthExpenses.total, finance.baseCurrency, locale)}
            hint={`${finance.monthExpenses.count} ×`}
            icon={<Receipt className="h-4 w-4" />}
          />
          {/* CR-001 §36 — collections are reported in the base currency, which
              is the only figure that can be added across currencies. */}
          <Metric
            label={t('reports.collectionsThisMonth')}
            value={formatMoney(finance.collectionsThisMonth?.localTotal ?? 0, finance.baseCurrency, locale)}
            hint={[...new Set<string>(finance.collectionsThisMonth?.receivedInCurrencies ?? [])].join(' · ')}
            tone="success"
          />
          <Metric
            label={t('clients.totalOutstanding')}
            value={formatMoney(finance.receivables?.overdue?.amount ?? 0, finance.baseCurrency, locale)}
            hint={`${finance.receivables?.overdue?.count ?? 0} ${t('dash.overdueInvoices').toLowerCase()}`}
            tone={(finance.receivables?.overdue?.count ?? 0) > 0 ? 'warning' : 'neutral'}
          />
          <Metric
            label={t('nav.payroll')}
            value={finance.payroll ? formatMoney(finance.payroll.total, finance.baseCurrency, locale) : '—'}
            hint={finance.payroll ? `${finance.payroll.employees} ${t('payroll.employees').toLowerCase()}` : undefined}
          />
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title={t('dash.expenseBreakdown')} />
            {finance.expenseBreakdown?.length ? (
              <ul className="space-y-3">
                {finance.expenseBreakdown.map((row: any) => {
                  const max = Math.max(...finance.expenseBreakdown.map((r: any) => r.total), 1);
                  return (
                    <li key={row.category?.id ?? 'unknown'}>
                      <div className="mb-1 flex items-baseline justify-between gap-3">
                        <span className="truncate text-body-sm">{localized(row.category)}</span>
                        <span className="shrink-0 text-body-sm tabular text-muted">
                          {formatMoney(row.total, finance.baseCurrency, locale)}
                        </span>
                      </div>
                      <ProgressBar percent={(row.total / max) * 100} />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-body-sm text-muted">{t('common.noResults')}</p>
            )}
          </Card>

          <Card padded={false}>
            <CardHeader title={t('dash.overdueInvoices')} className="p-5 pb-0" />
            {finance.overdueInvoices?.length ? (
              <Table>
                <thead>
                  <tr>
                    <Th>{t('tickets.number')}</Th>
                    <Th>{t('invoices.counterparty')}</Th>
                    <Th align="end">{t('invoices.remaining')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {finance.overdueInvoices.map((inv: any) => (
                    <Tr key={inv.id}>
                      <Td>
                        <Link to={`/invoices/${inv.id}`} className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                          {inv.number}
                        </Link>
                      </Td>
                      <Td className="truncate">
                        {inv.client ? (
                          <Link to={`/clients/${inv.client.id}`} className="hover:underline">
                            {locale === 'ar' && inv.client.companyNameAr
                              ? inv.client.companyNameAr
                              : inv.client.companyName}
                          </Link>
                        ) : (
                          inv.counterpartyName
                        )}
                      </Td>
                      <Td align="end" className="tabular text-error-fg dark:text-error-dark">
                        {formatMoney(inv.remainingAmount, inv.currency, locale)}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <EmptyState title={t('dash.nothingPending')} hint="" />
            )}
          </Card>
        </div>

        {/* §37 — invoiced, collected and outstanding as one line in the base
            currency, with the currencies they were raised in named beside it. */}
        {finance.receivables?.invoices > 0 && (
          <Card className="mt-4" padded={false}>
            <CardHeader
              title={t('reports.byCurrency')}
              subtitle={t('reports.inBaseCurrency', { currency: finance.receivables.currency })}
              className="p-5 pb-0"
            />
            <Table>
              <thead>
                <tr>
                  <Th>{t('reports.billedIn')}</Th>
                  <Th align="end">{t('reports.invoiced')}</Th>
                  <Th align="end">{t('reports.collected')}</Th>
                  <Th align="end">{t('reports.outstanding')}</Th>
                </tr>
              </thead>
              <tbody>
                <Tr>
                  <Td>
                    <span className="font-mono text-mono-sm text-muted">
                      {finance.receivables.billedInCurrencies.join('  ')}
                    </span>
                  </Td>
                  <Td align="end" className="tabular">
                    {formatMoney(finance.receivables.invoiced, finance.receivables.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular text-success-fg dark:text-success">
                    {formatMoney(finance.receivables.collected, finance.receivables.currency, locale)}
                  </Td>
                  <Td
                    align="end"
                    className={finance.receivables.outstanding > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular'}
                  >
                    {formatMoney(finance.receivables.outstanding, finance.receivables.currency, locale)}
                  </Td>
                </Tr>
              </tbody>
            </Table>
            {!!finance.receivables.unconvertedCurrencies?.length && (
              <p className="p-5 pt-3 text-caption text-warning-fg dark:text-warning-dark">
                {t('reports.noRateWarning', {
                  currencies: finance.receivables.unconvertedCurrencies.join(', '),
                })}
              </p>
            )}
          </Card>
        )}

        {/* §36 — where the money came from, ranked in the base currency. */}
        {(finance.receivables?.byClient?.length > 0 || finance.receivables?.byCountry?.length > 0) && (
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <CollectionsCard
              title={t('reports.byClient')}
              currency={finance.baseCurrency}
              rows={finance.receivables.byClient}
              labelOf={(r: any) => (locale === 'ar' && r.nameAr ? r.nameAr : r.name)}
              linkOf={(r: any) => `/clients/${r.id}`}
            />
            <CollectionsCard
              title={t('reports.byCountry')}
              currency={finance.baseCurrency}
              rows={finance.receivables.byCountry}
              labelOf={(r: any) => (locale === 'ar' && r.nameAr ? r.nameAr : r.name)}
            />
            <CollectionsCard
              title={t('reports.byProject')}
              currency={finance.baseCurrency}
              rows={finance.receivables.byProject}
              labelOf={(r: any) => `${r.code} — ${r.name}`}
              linkOf={(r: any) => `/projects/${r.id}`}
            />
          </div>
        )}

        {finance.budgets?.length > 0 && (
          <Card className="mt-4">
            <CardHeader title={t('dash.budgetUtilisation')} />
            <ul className="space-y-4">
              {finance.budgets.map((b: any) => (
                <li key={b.budgetId}>
                  <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-body-sm text-[var(--text-primary)]">{b.name}</span>
                    <span className="text-body-sm tabular text-muted">
                      {formatMoney(b.actualSpent, b.currency, locale)} /{' '}
                      {formatMoney(b.amount, b.currency, locale)} ·{' '}
                      <span
                        className={
                          b.limitExceeded
                            ? 'text-error-fg dark:text-error-dark'
                            : b.thresholdExceeded
                              ? 'text-warning-fg dark:text-warning-dark'
                              : 'text-success-fg dark:text-success'
                        }
                      >
                        {formatPercent(b.percentUsed, locale)}
                      </span>
                    </span>
                  </div>
                  <ProgressBar
                    percent={b.percentUsed}
                    tone={b.limitExceeded ? 'error' : b.thresholdExceeded ? 'warning' : 'success'}
                  />
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </>
  );
}

/** §36 — a ranked "collections by …" card. Every value is a base-currency total. */
function CollectionsCard({
  title,
  rows,
  currency,
  labelOf,
  linkOf,
}: {
  title: string;
  rows: any[];
  currency: string;
  labelOf: (row: any) => string;
  linkOf?: (row: any) => string;
}) {
  const { t, locale } = useI18n();
  if (!rows?.length) {
    return (
      <Card>
        <CardHeader title={title} />
        <p className="text-body-sm text-muted">{t('common.noResults')}</p>
      </Card>
    );
  }
  const max = Math.max(...rows.map((r) => r.local), 1);

  return (
    <Card>
      <CardHeader title={title} />
      <ul className="space-y-3">
        {rows.map((row, index) => (
          <li key={row.id ?? row.code ?? index}>
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="truncate text-body-sm">
                {linkOf ? (
                  <Link to={linkOf(row)} className="hover:underline">{labelOf(row)}</Link>
                ) : (
                  labelOf(row)
                )}
              </span>
              <span className="shrink-0 text-body-sm tabular text-muted">
                {formatMoney(row.local, currency, locale)}
              </span>
            </div>
            <ProgressBar percent={(row.local / max) * 100} />
          </li>
        ))}
      </ul>
    </Card>
  );
}
